/**
 * Game Helper Functions
 *
 * Provides general utility functions for game-wide logic, such as parsing room data,
 * managing game phases, and other common game operations.
 *
 * Integration:
 * - Used by: useGameStateContext / GameStateContext and GamePage.tsx.
 * - Depends on: game.types.ts, socket.types.ts.
 */

import type {
    GamePhase,
    GameState,
    RoomData,
    PlayerLetter,
    MonsterPosition,
} from '../types/game.types';

// ============================================================================
// Phase Helpers
// ============================================================================

/**
 * Convert phase string to numeric phase (for backward compatibility)
 * @param phase - Game phase string
 * @returns Numeric phase (0-4)
 */
export const phaseToNumber = (phase: GamePhase): number => {
    const phaseMap: Record<GamePhase, number> = {
        'LOBBY': 0,
        'ISLAND_PLACEMENT': 1,
        'MONSTER_PLACEMENT': 2,
        'ACTIVE': 3,
        'ENDED': 4,
    };
    return phaseMap[phase] ?? 0;
};

/**
 * Convert numeric phase to phase string
 * @param phaseNumber - Numeric phase (0-4)
 * @returns Game phase string
 */
export const numberToPhase = (phaseNumber: number): GamePhase => {
    const phaseMap: GamePhase[] = ['LOBBY', 'ISLAND_PLACEMENT', 'MONSTER_PLACEMENT', 'ACTIVE', 'ENDED'];
    return phaseMap[phaseNumber] ?? 'LOBBY';
};

/**
 * Check if phase transition is valid
 * @param fromPhase - Current phase
 * @param toPhase - Target phase
 * @returns True if transition is valid
 */
export const isValidPhaseTransition = (fromPhase: GamePhase, toPhase: GamePhase): boolean => {
    const validTransitions: Record<GamePhase, GamePhase[]> = {
        'LOBBY': ['ISLAND_PLACEMENT'],
        'ISLAND_PLACEMENT': ['MONSTER_PLACEMENT'],
        'MONSTER_PLACEMENT': ['ACTIVE'],
        'ACTIVE': ['ENDED'],
        'ENDED': [], // Can't transition from ended
    };

    return validTransitions[fromPhase]?.includes(toPhase) ?? false;
};

// ============================================================================
// Player Helpers
// ============================================================================

/**
 * Get opponent player letter
 * @param playerLetter - Current player letter
 * @returns Opponent player letter
 */
export const getOpponent = (playerLetter: PlayerLetter): PlayerLetter => {
    return playerLetter === 'A' ? 'B' : 'A';
};

/**
 * Check if it's a player's turn
 * @param gameState - Current game state
 * @param playerLetter - Player letter to check
 * @returns True if it's this player's turn
 */
export const isMyTurn = (gameState: GameState, playerLetter: PlayerLetter): boolean => {
    return gameState.turn.currentPlayer === playerLetter;
};

/**
 * Get player from game state
 * @param gameState - Current game state
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns Player data or null
 */
export const getPlayer = (gameState: GameState, playerLetter: PlayerLetter) => {
    return gameState.players[playerLetter] || null;
};

/**
 * Get my player data
 * @param gameState - Current game state
 * @param playerLetter - My player letter
 * @returns My player data or null
 */
export const getMyPlayer = (gameState: GameState, playerLetter: PlayerLetter) => {
    return getPlayer(gameState, playerLetter);
};

/**
 * Get opponent player data
 * @param gameState - Current game state
 * @param playerLetter - My player letter
 * @returns Opponent player data or null
 */
export const getOpponentPlayer = (gameState: GameState, playerLetter: PlayerLetter) => {
    const opponentLetter = getOpponent(playerLetter);
    return getPlayer(gameState, opponentLetter);
};

// ============================================================================
// Room Helpers
// ============================================================================

/**
 * Check if room is ready to start game
 * @param room - Room data
 * @returns True if all players are ready
 */
export const isRoomReady = (room: RoomData): boolean => {
    if (room.players.length < 2) {
        return false;
    }
    return room.players.every(player => player.ready);
};

/**
 * Get player from room
 * @param room - Room data
 * @param playerLetter - Player letter ('A' or 'B')
 * @returns Player data or null
 */
export const getRoomPlayer = (room: RoomData, playerLetter: PlayerLetter) => {
    return room.players.find(p => p.id === playerLetter) || null;
};

// ============================================================================
// Game State Helpers
// ============================================================================

/**
 * Check if game has ended
 * @param gameState - Current game state
 * @returns True if game has ended
 */
export const isGameEnded = (gameState: GameState): boolean => {
    return gameState.phase === 'ENDED' || gameState.status === 'COMPLETED' || gameState.status === 'ABANDONED';
};

/**
 * Check if game has a winner
 * @param gameState - Current game state
 * @returns True if there is a winner
 */
export const hasWinner = (gameState: GameState): boolean => {
    return gameState.winner !== null;
};

/**
 * Check if player is winner
 * @param gameState - Current game state
 * @param playerLetter - Player letter to check
 * @returns True if this player is the winner
 */
export const isWinner = (gameState: GameState, playerLetter: PlayerLetter): boolean => {
    return gameState.winner === playerLetter;
};

/** How the game ended — derived from `gameState` (history + board), not the socket event. */
export type InferredGameEndCondition = 'island_reached' | 'monsters_captured' | 'player_disconnect';

export interface GameOverDetailsFromState {
    winner: PlayerLetter;
    endCondition: InferredGameEndCondition;
    /** Full monster roster for each player (same shape as `gameState.players.*.monsters`). */
    playerAMonsters: MonsterPosition[];
    playerBMonsters: MonsterPosition[];
    /** Count of moves in `gameHistory.moves` made by each player. */
    movesByPlayer: { A: number; B: number };
    /** Total moves in the active-phase history (`movesByPlayer.A + movesByPlayer.B`). */
    totalMoves: number;
}

/**
 * Summarize game-over information from `gameState` for the end screen.
 * Returns `null` unless the game has ended with a winner.
 * Disconnect forfeits are inferred when the last recorded move does not match a natural win
 * (e.g. last move was not an island reach while the opponent still had monsters after a capture).
 */
export const getGameOverDetailsFromState = (state: GameState): GameOverDetailsFromState | null => {
    if (state.phase !== 'ENDED' || state.winner === null) {
        return null;
    }

    const moves = state.gameHistory.moves;
    const movesByPlayer = {
        A: moves.filter((m) => m.player === 'A').length,
        B: moves.filter((m) => m.player === 'B').length,
    };
    const totalMoves = moves.length;

    const last = moves.at(-1);
    let endCondition: InferredGameEndCondition = 'player_disconnect';

    if (last) {
        if (last.action === 'island') {
            endCondition = 'island_reached';
        } else if (last.action === 'capture') {
            const opponent = getOpponent(last.player);
            const opponentAlive = state.players[opponent].monsters.some((m) => m.alive);
            endCondition = opponentAlive ? 'player_disconnect' : 'monsters_captured';
        }
    }

    return {
        winner: state.winner,
        endCondition,
        playerAMonsters: state.players.A.monsters.map((m) => ({
            ...m,
            position: { ...m.position },
        })),
        playerBMonsters: state.players.B.monsters.map((m) => ({
            ...m,
            position: { ...m.position },
        })),
        movesByPlayer,
        totalMoves,
    };
};

// ============================================================================
// Utility Helpers
// ============================================================================

/**
 * Deep clone an object (simple JSON clone)
 * @param obj - Object to clone
 * @returns Cloned object
 */
export const deepClone = <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
};

/**
 * Delay helper for async operations
 * @param ms - Milliseconds to delay
 * @returns Promise that resolves after delay
 */
export const delay = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};
